import React, { useEffect, useMemo, useState } from "react";
import { navigate } from "@reach/router";
import "./css/dashboard.scss";

import StandardTemplate from "../templates/standard_template/standard_template";
import DashboardService, { isDashboardEnabled } from "../services/dashboard_service";
import DocumentService from "../services/document_service";
import DOCUMENT_TYPES from "../constants/document_types";
import { useUser } from "../hooks/user";
import config from "../config/config.js";

const DASHBOARD_VERSION = "v1.0";

// ---- demo what-if simulator (Dev/Demo build only) ------------------------------
// Re-decides every closed/lost loan so each producer's Submit-to-Close matches the
// entered rate; every KPI, chart and drill-down then recalculates from the result.
// Deterministic (same rate -> same picture) via a per-loan pseudo-random score.
function loanScore(i) {
  const x = Math.sin((i + 1) * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}
function simulateLoans(loans, ratePct) {
  const rate = Math.min(100, Math.max(0, ratePct)) / 100;
  const groups = {};
  loans.forEach((l, i) => {
    if (l.stage !== "Closed" && l.stage !== "Lost") return;
    const key = l.brokerLoId != null ? "b" + l.brokerLoId : "u" + l.ownerUserId;
    (groups[key] = groups[key] || []).push(i);
  });
  const stage = {};
  Object.values(groups).forEach(idx => {
    idx.sort((a, b) => loanScore(a) - loanScore(b));
    idx.forEach((i, rank) => {
      stage[i] = (rank + 0.5) / idx.length < rate ? "Closed" : "Lost";
    });
  });
  return loans.map((l, i) => (stage[i] ? { ...l, stage: stage[i] } : l));
}

const OPEN_STAGES = ["Leads", "In Process", "Underwriting", "Docs Out", "Funding"];
const FUNNEL_STAGES = [...OPEN_STAGES, "Closed"];
const STAGE_COLORS = {
  Leads: "#9db4d0",
  "In Process": "#6f96c4",
  Underwriting: "#4a7ab5",
  "Docs Out": "#2f5e9e",
  Funding: "#1f4886",
  Closed: "#14315c",
  Lost: "#b23b3b"
};

const RETAIL = "Retail";
const WHOLESALE = "Wholesale";

const MONTH_COLORS = [
  "#14315c", "#1f4886", "#2f5e9e", "#4a7ab5", "#6f96c4", "#9db4d0",
  "#2e8b57", "#3a9d6a", "#57b285", "#8a6d3b", "#a9853f", "#c0392b"
];

const TXN_COLORS = { Purchase: "#14315c", Refinance: "#2e8b57" };

const MORTGAGE_TYPES = ["Conventional", "FHA", "VA", "Non-QM", "Jumbo"];
const MORTGAGE_COLORS = {
  Conventional: "#2f5e9e",
  FHA: "#2e8b57",
  VA: "#8a6d3b",
  "Non-QM": "#8e44ad",
  Jumbo: "#c0392b"
};

// ---- date helpers (all dates are "YYYY-MM-DD") --------------------------------
function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function periodRange(asOf, period) {
  const end = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  let start;
  if (period === "today") start = new Date(end);
  else if (period === "month") start = new Date(end.getFullYear(), end.getMonth(), 1);
  else if (period === "qtr") {
    const q = Math.floor(end.getMonth() / 3);
    start = new Date(end.getFullYear(), q * 3, 1);
  } else start = new Date(end.getFullYear(), 0, 1); // ytd
  return { start, end };
}
function priorRange(asOf, period) {
  const { start, end } = periodRange(asOf, period);
  const spanDays = Math.round((end - start) / 86400000) + 1;
  const priorEnd = new Date(start);
  priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - (spanDays - 1));
  return { start: priorStart, end: priorEnd };
}
function inRange(date, range) {
  return date && date >= range.start && date <= range.end;
}

// ---- number formatting -------------------------------------------------------
function fmtMoney(n) {
  if (!isFinite(n)) return "$0";
  if (n >= 1000000) return "$" + (n / 1000000).toFixed(2) + "M";
  if (n >= 1000) return "$" + Math.round(n / 1000) + "K";
  return "$" + Math.round(n);
}
// Full dollar amount with commas (e.g. $5,980,000) for the KPI cards, so
// managers read a consistent exact figure. Breakdown columns keep fmtMoney.
function fmtMoneyFull(n) {
  if (!isFinite(n)) return "$0";
  return "$" + Math.round(n).toLocaleString("en-US");
}
function fmtPct(n) {
  return isFinite(n) ? n.toFixed(1) + "%" : "—";
}
// "YYYY-MM-DD" -> "MM/DD/YYYY" (LANIS display format).
function fmtDate(s) {
  if (!s) return "—";
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return m + "/" + d + "/" + y;
}

// ---- rollup over a set of loans ---------------------------------------------
function rollup(loans, range) {
  let closed = 0, lost = 0, fundedVol = 0, creditSum = 0, creditN = 0;
  const stageCounts = {};
  FUNNEL_STAGES.forEach(s => (stageCounts[s] = 0));
  let openUnits = 0, openVol = 0, stalled = 0;
  loans.forEach(l => {
    const decided = l.stage === "Closed" || l.stage === "Lost";
    if (decided) {
      if (inRange(parseDate(l.closedDate), range)) {
        if (l.stage === "Closed") {
          closed++; fundedVol += l.amount || 0;
          if (l.credit) { creditSum += l.credit; creditN++; }
          stageCounts.Closed++;
        } else lost++;
      }
    } else {
      // open pipeline = current snapshot
      openUnits++; openVol += l.amount || 0;
      if (l.stalled) stalled++;
      if (stageCounts[l.stage] != null) stageCounts[l.stage]++;
    }
  });
  const s2c = closed + lost > 0 ? (closed / (closed + lost)) * 100 : NaN;
  const avgCredit = creditN > 0 ? Math.round(creditSum / creditN) : NaN;
  return { closed, lost, s2c, fundedVol, avgCredit, stageCounts, openUnits, openVol, stalled };
}

// Statuses that mean the loan has NOT yet been submitted to underwriting. These
// age on the "In Process" clock (from startedDate); everything else ages on the
// "Days Since Submit to U/W" clock (from submittedDate). Matches Randy's split.
const PRE_SUBMISSION_STATUSES = new Set([
  "18", "19", "20", "21", "22", "23", "26", "28", "29"
]);
function isPreSubmission(loan) {
  return PRE_SUBMISSION_STATUSES.has(String(loan.status || ""));
}
function daysSince(dateStr, asOf) {
  const d = parseDate(dateStr);
  if (!d) return null;
  return Math.max(0, Math.round((asOf - d) / 86400000));
}

// Roll up lost competing scenarios (VISION Lost-Loan Intelligence) for a scope +
// period. Losses are dated by decisionDate. Rate gap = our rate - their rate.
function aggregateLost(rows, range) {
  const s = {
    vol: 0, count: 0, pricing: 0, gapSum: 0, gapN: 0,
    byLender: {}, pV: 0, rV: 0, pN: 0, rN: 0, rows: []
  };
  (rows || []).forEach(l => {
    if (!inRange(parseDate(l.decisionDate), range)) return;
    s.count++;
    s.vol += l.amount || 0;
    if (/pricing/i.test(l.reason || "")) s.pricing++;
    const lender = (l.competitor || "—").trim() || "—";
    s.byLender[lender] = (s.byLender[lender] || 0) + (l.amount || 0);
    if ((l.loanType || "") === "Refinance") { s.rV += l.amount || 0; s.rN++; }
    else { s.pV += l.amount || 0; s.pN++; }
    if (l.ourRate != null && l.theirRate != null) {
      s.gapSum += l.ourRate - l.theirRate; s.gapN++;
    }
    s.rows.push(l);
  });
  s.avgGap = s.gapN ? s.gapSum / s.gapN : null;
  s.pricingPct = s.count ? Math.round((s.pricing / s.count) * 100) : 0;
  s.lenderOrder = Object.keys(s.byLender).sort((a, b) => s.byLender[b] - s.byLender[a]);
  s.topLender = s.lenderOrder[0] || "—";
  s.topLenderVol = s.lenderOrder[0] ? s.byLender[s.lenderOrder[0]] : 0;
  s.rows.sort((a, b) => (b.amount || 0) - (a.amount || 0));
  return s;
}

function trend(cur, prior) {
  if (!isFinite(cur) || !isFinite(prior) || prior === 0) return null;
  return cur - prior; // positive = up
}

export default function DashboardPage() {
  const [{ accountProfile, companyProfile }] = useUser();
  const enabled = isDashboardEnabled(accountProfile, companyProfile);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(RETAIL); // VIEW=Retail, VISION=Wholesale
  const [period, setPeriod] = useState("ytd");
  const [chart, setChart] = useState("bar");
  const [typeChart, setTypeChart] = useState("bar");
  const [txnChart, setTxnChart] = useState("bar");
  const [monthChart, setMonthChart] = useState("bar");
  const [stack, setStack] = useState([]); // drill-down breadcrumb of person ids
  const [search, setSearch] = useState("");
  const [navSearch, setNavSearch] = useState(""); // global jump-to-person search
  const [typeFilter, setTypeFilter] = useState(null); // mortgage-type filter
  const [myDocs, setMyDocs] = useState(null); // signed-in user's real loan files
  // Demo what-if simulator (config.enableDemoSimulator, Dev only).
  const [simInput, setSimInput] = useState("");
  const [simRate, setSimRate] = useState(null); // null = real data
  const [simCompare, setSimCompare] = useState(null); // previous calculation, same view

  const userId = useMemo(() => {
    try {
      const t = localStorage.access_token;
      const payload = JSON.parse(atob(t.split(".")[1]));
      return payload.userId;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    DashboardService.getDashboardData()
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setError(String(e && e.message ? e.message : e)); });
    // The signed-in user's real, editable loan files (for drill-down from the
    // dashboard into an actual file). Seeded aggregate loans have no file, so
    // clickable drill-down is offered only for the viewer's own book.
    DocumentService.getPipelineData()
      .then(docs => { if (live) setMyDocs(Array.isArray(docs) ? docs : []); })
      .catch(() => { if (live) setMyDocs([]); });
    return () => { live = false; };
  }, [enabled]);

  // --- org maps -------------------------------------------------------------
  const model = useMemo(() => {
    if (!data) return null;
    const people = data.people || [];
    const byId = {};
    people.forEach(p => (byId[p.id] = p));
    const childrenOf = {};
    people.forEach(p => {
      const pid = p.reportsToId;
      if (pid != null) (childrenOf[pid] = childrenOf[pid] || []).push(p);
    });
    function subtreeIds(rootId) {
      const out = new Set([rootId]);
      const walk = id => (childrenOf[id] || []).forEach(c => { out.add(c.id); walk(c.id); });
      walk(rootId);
      return out;
    }
    const viewer = byId[userId] || people.find(p => p.role === "CEO") || people[0];
    const isExec = viewer && ["CEO", "EVP Production", ""].includes(viewer.role);
    const canBoth = isExec || (viewer && viewer.product === "Both");
    const retailTop = people.find(p => p.role === "CEO") ||
      people.find(p => p.reportsToId == null && p.product !== WHOLESALE);
    // Top of the wholesale chain = the highest wholesale node whose manager is
    // NOT wholesale (i.e., reports up into the CEO/exec), so an exec's VISION
    // view starts at the top RVPs and drills RVP -> Sr AE -> AE. Fall back to any
    // wholesale person.
    const wholesaleRoots = people.filter(p => {
      if (p.product !== WHOLESALE) return false;
      const mgr = p.reportsToId != null ? byId[p.reportsToId] : null;
      return !mgr || mgr.product !== WHOLESALE;
    });
    const wholesaleTop =
      (retailTop && retailTop) || // CEO ("Both") sits above the wholesale RVPs
      wholesaleRoots[0] ||
      people.find(p => p.product === WHOLESALE);
    // Wholesale broker structure (below the AE): Broker Companies + their LOs.
    const brokerCompanyById = {};
    (data.brokerCompanies || []).forEach(c => (brokerCompanyById[c.id] = c));
    const brokerLoById = {};
    (data.brokerLos || []).forEach(l => (brokerLoById[l.id] = l));
    return {
      people, byId, childrenOf, subtreeIds, viewer, isExec, canBoth,
      retailTop, wholesaleTop, brokerCompanyById, brokerLoById
    };
  }, [data, userId]);

  // set initial mode + root once model is ready
  useEffect(() => {
    if (!model) return;
    const startMode = model.viewer && model.viewer.product === WHOLESALE ? WHOLESALE : RETAIL;
    setMode(startMode);
  }, [model]);

  const rootId = useMemo(() => {
    if (!model) return null;
    if (mode === RETAIL) {
      return model.viewer && model.viewer.product !== WHOLESALE && !model.isExec
        ? model.viewer.id
        : (model.retailTop ? model.retailTop.id : model.viewer.id);
    }
    return model.viewer && model.viewer.product === WHOLESALE && !model.isExec
      ? model.viewer.id
      : (model.wholesaleTop ? model.wholesaleTop.id : (model.viewer ? model.viewer.id : null));
  }, [model, mode]);

  // reset drill path when the root changes (initial load / mode change for a
  // single-product viewer). goToPerson sets the stack directly afterward.
  useEffect(() => {
    setStack(rootId != null ? [rootId] : []);
    setSearch("");
    setTypeFilter(null);
  }, [rootId]);

  if (!enabled) {
    return (
      <StandardTemplate basic showMoreLinks className="dashboard-template">
        <div className="dashboard-error">
          <h2>Dashboard not available</h2>
          <p>The VIEW / VISION dashboard isn&#39;t enabled for this account.</p>
        </div>
      </StandardTemplate>
    );
  }
  if (error) {
    return (
      <StandardTemplate basic showMoreLinks className="dashboard-template">
        <div className="dashboard-error">
          <h2>Dashboard couldn&#39;t load</h2>
          <p>{error}</p>
        </div>
      </StandardTemplate>
    );
  }
  if (!data || !model || stack.length === 0) {
    return (
      <StandardTemplate basic showMoreLinks className="dashboard-template">
        <div className="dashboard-loading">Loading dashboard…</div>
      </StandardTemplate>
    );
  }

  const asOf = parseDate(data.asOf) || new Date();
  const allLoans =
    config.enableDemoSimulator && simRate != null
      ? simulateLoans(data.loans || [], simRate)
      : data.loans || [];
  const currentId = stack[stack.length - 1];
  const productFilter = mode === RETAIL ? RETAIL : WHOLESALE;
  const ownerKey = mode === RETAIL ? "ownerUserId" : "aeUserId";

  // A drill node is a person (retail/wholesale org), OR — below a wholesale AE —
  // a Broker Company ("co:<id>") or a Broker LO ("blo:<id>").
  const nodeType = String(currentId).startsWith("co:")
    ? "brokerCompany"
    : String(currentId).startsWith("blo:")
    ? "brokerLo"
    : "person";
  const rawId =
    nodeType === "person" ? currentId : Number(String(currentId).split(":")[1]);
  // The AE in context while inside the broker levels = the last person in the
  // stack (the broker company/LO belong to that AE's book).
  const aeStackId = [...stack]
    .reverse()
    .find(s => !String(s).startsWith("co:") && !String(s).startsWith("blo:"));
  // "Own book" = the signed-in user is viewing their own node (retail LO or
  // wholesale AE). Their real loan files then show as a click-to-open list at the
  // top of their dashboard — same capability as retail VIEW (view-only for an AE).
  const isOwnBook = currentId === userId;

  // Loans in scope for the current node (drives the top KPI cards + breakdowns).
  let scopedLoans;
  if (nodeType === "brokerCompany") {
    scopedLoans = allLoans.filter(
      l =>
        l.product === WHOLESALE &&
        l.aeUserId === aeStackId &&
        l.brokerCompanyId === rawId
    );
  } else if (nodeType === "brokerLo") {
    // Scope to the AE in context too: a broker LO who works through more than one
    // AE/lender must never surface another AE's loans in this AE's drill path.
    scopedLoans = allLoans.filter(
      l =>
        l.product === WHOLESALE &&
        l.brokerLoId === rawId &&
        l.aeUserId === aeStackId
    );
  } else {
    const scopeIds = model.subtreeIds(currentId);
    scopedLoans = allLoans.filter(
      l => l.product === productFilter && scopeIds.has(l[ownerKey])
    );
  }
  // Apply the mortgage-type filter (if any) to everything except the by-type
  // breakdown itself (which always shows all types so you can pick one).
  const typedLoans = typeFilter
    ? scopedLoans.filter(l => l.mortgageType === typeFilter)
    : scopedLoans;

  // Lost competing scenarios in the same scope (VISION Lost-Loan Intelligence).
  const allLost = data.lostScenarios || [];
  let scopedLost;
  if (nodeType === "brokerCompany") {
    scopedLost = allLost.filter(
      l => l.product === WHOLESALE && l.aeUserId === aeStackId && l.brokerCompanyId === rawId
    );
  } else if (nodeType === "brokerLo") {
    scopedLost = allLost.filter(
      l => l.product === WHOLESALE && l.brokerLoId === rawId && l.aeUserId === aeStackId
    );
  } else {
    const scopeIds = model.subtreeIds(currentId);
    scopedLost = allLost.filter(
      l => l.product === productFilter && scopeIds.has(l[ownerKey])
    );
  }

  const range = periodRange(asOf, period);
  const prior = priorRange(asOf, period);
  const cur = rollup(typedLoans, range);
  const prev = rollup(typedLoans, prior);
  const lostAgg = aggregateLost(scopedLost, range);

  // funded-in-period rollup per mortgage type (for the breakdown)
  const typeBreakdown = MORTGAGE_TYPES.map(t => {
    const r = rollup(
      scopedLoans.filter(l => l.mortgageType === t),
      range
    );
    return { type: t, units: r.closed, volume: r.fundedVol };
  });
  const maxTypeUnits = Math.max(1, ...typeBreakdown.map(t => t.units));

  // Purchase vs Refinance mix (funded in period) — direction-of-business KPI.
  const txnBreakdown = ["Purchase", "Refinance"].map(t => {
    const r = rollup(scopedLoans.filter(l => l.loanType === t), range);
    return { type: t, units: r.closed, volume: r.fundedVol };
  });
  const txnTotalUnits = txnBreakdown.reduce((a, b) => a + b.units, 0);
  const maxTxnUnits = Math.max(1, ...txnBreakdown.map(t => t.units));

  // Monthly breakdown of funded loans across the selected period (shown for
  // Quarter and YTD so a quarterly/annual number reads as its months).
  const showMonthly = period === "qtr" || period === "ytd";
  let monthlyBreakdown = [];
  if (showMonthly) {
    const months = [];
    let cursor = new Date(range.start.getFullYear(), range.start.getMonth(), 1);
    while (cursor <= range.end) {
      months.push(new Date(cursor));
      cursor.setMonth(cursor.getMonth() + 1);
    }
    monthlyBreakdown = months.map(m => {
      const mStart = new Date(m.getFullYear(), m.getMonth(), 1);
      const mEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0);
      const r = rollup(typedLoans, { start: mStart, end: mEnd });
      return {
        label:
          m.toLocaleString("en-US", { month: "short" }) +
          " " +
          String(m.getFullYear()).slice(2),
        units: r.closed,
        volume: r.fundedVol
      };
    });
  }
  const maxMonthUnits = Math.max(1, ...monthlyBreakdown.map(x => x.units));

  function personRollup(p) {
    const ids = model.subtreeIds(p.id);
    const loans = allLoans.filter(
      l =>
        l.product === productFilter &&
        ids.has(l[ownerKey]) &&
        (!typeFilter || l.mortgageType === typeFilter)
    );
    return rollup(loans, range);
  }

  function typed(loans) {
    return typeFilter ? loans.filter(l => l.mortgageType === typeFilter) : loans;
  }

  // Display name for any drill node (person / broker company / broker LO).
  function nodeName(id) {
    if (String(id).startsWith("co:")) {
      const c = model.brokerCompanyById[Number(String(id).split(":")[1])];
      return c ? c.companyName : "Broker Company";
    }
    if (String(id).startsWith("blo:")) {
      const b = model.brokerLoById[Number(String(id).split(":")[1])];
      return b ? b.name : "Broker LO";
    }
    const p = model.byId[id];
    return p ? p.name : "?";
  }
  function nodeRole(id) {
    if (String(id).startsWith("co:")) return "Broker Company";
    if (String(id).startsWith("blo:")) return "Broker LO";
    const p = model.byId[id];
    return p ? p.role : "";
  }

  // The rows shown under the current node. People (org chart) until we reach a
  // wholesale AE, then Broker Companies -> Broker LOs -> loans. Each row carries
  // a rollup + a drill target so the same leaderboard renders all three levels.
  let drillItems = null; // null => show the loan list (a leaf)
  if (nodeType === "person") {
    const reports = (model.childrenOf[currentId] || []).filter(p =>
      mode === RETAIL ? p.product !== WHOLESALE : p.product === WHOLESALE
    );
    if (reports.length > 0) {
      drillItems = reports.map(p => ({
        key: p.id,
        name: p.name,
        role: p.role,
        region: p.region,
        r: personRollup(p),
        drillTo: () => setStack([...stack, p.id])
      }));
    } else if (mode === WHOLESALE) {
      // AE leaf -> that AE's Broker Companies.
      const coIds = [
        ...new Set(
          allLoans
            .filter(l => l.product === WHOLESALE && l.aeUserId === currentId)
            .map(l => l.brokerCompanyId)
            .filter(id => id != null)
        )
      ];
      drillItems = coIds.map(coId => {
        const co = model.brokerCompanyById[coId];
        const loans = typed(
          allLoans.filter(
            l =>
              l.product === WHOLESALE &&
              l.aeUserId === currentId &&
              l.brokerCompanyId === coId
          )
        );
        return {
          key: "co:" + coId,
          name: co ? co.companyName : "Broker Company " + coId,
          role: "Broker Company",
          region: co ? co.region : "",
          r: rollup(loans, range),
          drillTo: () => setStack([...stack, "co:" + coId])
        };
      });
    }
  } else if (nodeType === "brokerCompany") {
    // Broker Company -> its Broker LOs (for the AE in context).
    const bloIds = [
      ...new Set(
        allLoans
          .filter(
            l =>
              l.product === WHOLESALE &&
              l.aeUserId === aeStackId &&
              l.brokerCompanyId === rawId
          )
          .map(l => l.brokerLoId)
          .filter(id => id != null)
      )
    ];
    drillItems = bloIds.map(bloId => {
      const blo = model.brokerLoById[bloId];
      const loans = typed(
        allLoans.filter(
          l =>
            l.product === WHOLESALE &&
            l.brokerLoId === bloId &&
            l.aeUserId === aeStackId
        )
      );
      return {
        key: "blo:" + bloId,
        name: blo ? blo.name : "Broker LO " + bloId,
        role: "Broker LO",
        region: "",
        r: rollup(loans, range),
        drillTo: () => setStack([...stack, "blo:" + bloId])
      };
    });
  }
  // nodeType === "brokerLo" => drillItems stays null => loan list.
  // An empty level (no companies/LOs) falls through to the loan list.
  if (drillItems && drillItems.length === 0) {
    drillItems = null;
  }

  const searchLc = search.trim().toLowerCase();
  const shownReports =
    drillItems && searchLc
      ? drillItems.filter(
          it =>
            it.name.toLowerCase().includes(searchLc) ||
            (it.region || "").toLowerCase().includes(searchLc) ||
            (it.role || "").toLowerCase().includes(searchLc)
        )
      : drillItems;

  // Global search: match ANY person in the company and jump straight to them,
  // switching VIEW/VISION as needed and rebuilding the breadcrumb path.
  const navSearchLc = navSearch.trim().toLowerCase();
  const navMatches = navSearchLc
    ? model.people
        .filter(
          p =>
            p.name.toLowerCase().includes(navSearchLc) ||
            (p.region || "").toLowerCase().includes(navSearchLc) ||
            (p.role || "").toLowerCase().includes(navSearchLc)
        )
        .slice(0, 8)
    : [];

  function goToPerson(p) {
    const path = [];
    let cur = p;
    while (cur) {
      path.unshift(cur.id);
      cur = cur.reportsToId != null ? model.byId[cur.reportsToId] : null;
    }
    const targetMode = p.product === WHOLESALE ? WHOLESALE : RETAIL;
    setNavSearch("");
    if (model.canBoth) {
      setMode(targetMode);
    }
    setStack(path);
  }

  // What-if simulator: compare against the previous calculation for the SAME
  // view (same node, period, mode and type filter).
  const simViewKey = [mode, period, currentId, typeFilter || ""].join("|");
  function runSimulation() {
    const rate = parseFloat(String(simInput).replace("%", ""));
    if (!isFinite(rate) || rate < 0 || rate > 100) return;
    setSimCompare({
      key: simViewKey,
      label: simRate == null ? "Actual" : simRate + "%",
      closed: cur.closed,
      fundedVol: cur.fundedVol
    });
    setSimRate(rate);
  }
  function resetSimulation() {
    setSimRate(null);
    setSimCompare(null);
    setSimInput("");
  }
  const showSimCompare =
    simRate != null && simCompare && simCompare.key === simViewKey;

  const s2cTrend = trend(cur.s2c, prev.s2c);
  const volTrend = trend(cur.fundedVol, prev.fundedVol);
  const creditTrend = trend(cur.avgCredit, prev.avgCredit);

  const maxStage = Math.max(1, ...FUNNEL_STAGES.map(s => cur.stageCounts[s]));
  const funnelTotal = FUNNEL_STAGES.reduce((a, s) => a + cur.stageCounts[s], 0) || 1;

  function TrendArrow({ value, unit }) {
    if (value == null || value === 0) return <span className="trend flat">—</span>;
    const up = value > 0;
    const magnitude =
      unit === "$"
        ? fmtMoneyFull(Math.abs(value))
        : Math.abs(value).toFixed(1);
    return (
      <span className={"trend " + (up ? "up" : "down")}>
        {up ? "▲" : "▼"} {magnitude}
        {unit === "%" ? " pts" : ""}
      </span>
    );
  }

  return (
    <StandardTemplate basic showMoreLinks className="dashboard-template">
      <div className="dashboard">
        <div className="dashboard-header">
          <div className="dashboard-title">
            <h1>{mode === RETAIL ? "VIEW" : "VISION"}</h1>
            <span className="dashboard-sub">
              {mode === RETAIL ? "Retail" : "Wholesale"} · {data.company} · as of{" "}
              {fmtDate(data.asOf)} <span className="version">{DASHBOARD_VERSION}</span>
            </span>
          </div>
          {model.canBoth && (
            <div className="dashboard-mode-toggle">
              <button
                className={"button" + (mode === RETAIL ? " active" : "")}
                onClick={() => setMode(RETAIL)}
              >
                VIEW · Retail
              </button>
              <button
                className={"button" + (mode === WHOLESALE ? " active" : "")}
                onClick={() => setMode(WHOLESALE)}
              >
                VISION · Wholesale
              </button>
            </div>
          )}
        </div>

        <div className="dashboard-topbar">
          <div className="dashboard-periods">
            {[
              ["today", "Today"],
              ["month", "Month"],
              ["qtr", "Quarter"],
              ["ytd", "YTD"]
            ].map(([key, label]) => (
              <button
                key={key}
                className={"button" + (period === key ? " active" : "")}
                onClick={() => setPeriod(key)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="dashboard-global-search">
            <input
              className="people-search"
              placeholder="Search anyone (name / region / role)…"
              value={navSearch}
              onChange={e => setNavSearch(e.target.value)}
            />
            {navMatches.length > 0 && (
              <ul className="global-search-results">
                {navMatches.map(p => (
                  <li key={p.id} onClick={() => goToPerson(p)}>
                    <span className="gs-name">{p.name}</span>
                    <span className="gs-meta">
                      {p.role}
                      {p.region ? " · " + p.region : ""} ·{" "}
                      {p.product === WHOLESALE ? "VISION" : "VIEW"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {config.enableDemoSimulator && (
          <div className={"demo-sim" + (simRate != null ? " active" : "")}>
            <label className="demo-sim-question" htmlFor="demo-sim-rate">
              What is your Submit-to-Close ratio?
            </label>
            <input
              id="demo-sim-rate"
              className="demo-sim-input"
              type="number"
              min="0"
              max="100"
              placeholder="63"
              value={simInput}
              onChange={e => setSimInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") runSimulation();
              }}
            />
            <span className="demo-sim-pct">%</span>
            <button className="button" onClick={runSimulation}>
              Calculate Now
            </button>
            {simRate != null && (
              <button className="button" onClick={resetSimulation}>
                Reset
              </button>
            )}
            {simRate != null && (
              <span className="demo-sim-badge">
                Showing results at {simRate}% Submit-to-Close
              </span>
            )}
            {showSimCompare && (
              <div className="demo-sim-compare">
                At <strong>{simRate}%</strong>: {cur.closed} loans closed,{" "}
                {fmtMoneyFull(cur.fundedVol)} funded. At{" "}
                <strong>{simCompare.label}</strong>: {simCompare.closed} loans,{" "}
                {fmtMoneyFull(simCompare.fundedVol)}.{" "}
                <span
                  className={
                    cur.fundedVol >= simCompare.fundedVol ? "demo-sim-up" : "demo-sim-down"
                  }
                >
                  Difference: {cur.closed - simCompare.closed >= 0 ? "+" : ""}
                  {cur.closed - simCompare.closed} loans,{" "}
                  {cur.fundedVol - simCompare.fundedVol >= 0 ? "+" : "-"}
                  {fmtMoneyFull(Math.abs(cur.fundedVol - simCompare.fundedVol))}
                </span>
              </div>
            )}
          </div>
        )}

        {/* breadcrumb */}
        <div className="dashboard-crumbs">
          {stack.map((id, i) => {
            const last = i === stack.length - 1;
            const role = nodeRole(id);
            return (
              <span key={id}>
                {i > 0 && <span className="crumb-sep">›</span>}
                <button
                  className="crumb"
                  disabled={last}
                  onClick={() => setStack(stack.slice(0, i + 1))}
                >
                  {nodeName(id)}
                  {role ? " (" + role + ")" : ""}
                </button>
              </span>
            );
          })}
        </div>

        {/* KPI cards */}
        <div className="dashboard-kpis">
          <div className="kpi kpi-lead">
            <div className="kpi-label">Submit → Close</div>
            <div className="kpi-value">{fmtPct(cur.s2c)}</div>
            <div className="kpi-foot">
              <TrendArrow value={s2cTrend} unit="%" />
              <span className="kpi-detail">
                {cur.closed} closed / {cur.lost} lost
              </span>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Funded Volume</div>
            <div className="kpi-value">{fmtMoneyFull(cur.fundedVol)}</div>
            <div className="kpi-foot">
              <TrendArrow value={volTrend} unit="$" />
              <span className="kpi-detail">{cur.closed} units</span>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Avg Credit</div>
            <div className="kpi-value">{isFinite(cur.avgCredit) ? cur.avgCredit : "—"}</div>
            <div className="kpi-foot">
              <TrendArrow value={creditTrend} unit="pts" />
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Open Pipeline</div>
            <div className="kpi-value">{cur.openUnits}</div>
            <div className="kpi-foot">
              <span className="kpi-detail">{fmtMoneyFull(cur.openVol)}</span>
              {cur.stalled > 0 && <span className="kpi-warn">{cur.stalled} stalled</span>}
            </div>
          </div>
        </div>

        {/* Purchase vs Refinance (direction of business) */}
        <div className="dashboard-types">
          <div className="types-head">
            <h3>
              Purchase vs Refinance (funded, {period.toUpperCase()}) —{" "}
              {txnTotalUnits > 0
                ? Math.round((txnBreakdown[0].units / txnTotalUnits) * 100) +
                  "% Purchase"
                : "—"}
            </h3>
            <div className="funnel-toggle">
              <button
                className={"button" + (txnChart === "bar" ? " active" : "")}
                onClick={() => setTxnChart("bar")}
              >
                Bar
              </button>
              <button
                className={"button" + (txnChart === "pie" ? " active" : "")}
                onClick={() => setTxnChart("pie")}
              >
                Pie
              </button>
            </div>
          </div>
          {txnChart === "bar" ? (
            <div className="type-bars">
              {txnBreakdown.map(t => (
                <div key={t.type} className="type-chip static">
                  <span className="type-name">{t.type}</span>
                  <span className="type-track">
                    <span
                      className="type-fill"
                      style={{
                        width: (t.units / maxTxnUnits) * 100 + "%",
                        background: TXN_COLORS[t.type]
                      }}
                    />
                  </span>
                  <span className="type-count num">{t.units}</span>
                  <span className="type-vol num">{fmtMoney(t.volume)}</span>
                </div>
              ))}
            </div>
          ) : (
            <DonutChart
              items={txnBreakdown.map(t => ({
                label: t.type,
                value: t.units,
                color: TXN_COLORS[t.type]
              }))}
            />
          )}
        </div>

        {/* loan-type breakdown + filter */}
        <div className="dashboard-types">
          <div className="types-head">
            <h3>By Loan Type (funded, {period.toUpperCase()})</h3>
            <div className="funnel-toggle">
              {typeFilter && (
                <button className="button" onClick={() => setTypeFilter(null)}>
                  Clear: {typeFilter} ✕
                </button>
              )}
              <button
                className={"button" + (typeChart === "bar" ? " active" : "")}
                onClick={() => setTypeChart("bar")}
              >
                Bar
              </button>
              <button
                className={"button" + (typeChart === "pie" ? " active" : "")}
                onClick={() => setTypeChart("pie")}
              >
                Pie
              </button>
            </div>
          </div>
          {typeChart === "bar" ? (
            <div className="type-bars">
              {typeBreakdown.map(t => (
                <button
                  key={t.type}
                  className={
                    "type-chip" + (typeFilter === t.type ? " selected" : "")
                  }
                  onClick={() =>
                    setTypeFilter(typeFilter === t.type ? null : t.type)
                  }
                  title="Filter the whole dashboard by this loan type"
                >
                  <span className="type-name">{t.type}</span>
                  <span className="type-track">
                    <span
                      className="type-fill"
                      style={{
                        width: (t.units / maxTypeUnits) * 100 + "%",
                        background: MORTGAGE_COLORS[t.type]
                      }}
                    />
                  </span>
                  <span className="type-count num">{t.units}</span>
                  <span className="type-vol num">{fmtMoney(t.volume)}</span>
                </button>
              ))}
            </div>
          ) : (
            <DonutChart
              items={typeBreakdown.map(t => ({
                label: t.type,
                value: t.units,
                color: MORTGAGE_COLORS[t.type],
                onClick: () =>
                  setTypeFilter(typeFilter === t.type ? null : t.type),
                selected: typeFilter === t.type
              }))}
            />
          )}
        </div>

        {/* monthly breakdown (Quarter / YTD) */}
        {showMonthly && (
          <div className="dashboard-types">
            <div className="types-head">
              <h3>By Month (funded, {period.toUpperCase()})</h3>
              <div className="funnel-toggle">
                <button
                  className={"button" + (monthChart === "bar" ? " active" : "")}
                  onClick={() => setMonthChart("bar")}
                >
                  Bar
                </button>
                <button
                  className={"button" + (monthChart === "pie" ? " active" : "")}
                  onClick={() => setMonthChart("pie")}
                >
                  Pie
                </button>
              </div>
            </div>
            {monthChart === "bar" ? (
              <div className="type-bars">
                {monthlyBreakdown.map(m => (
                  <div key={m.label} className="type-chip static">
                    <span className="type-name">{m.label}</span>
                    <span className="type-track">
                      <span
                        className="type-fill"
                        style={{
                          width: (m.units / maxMonthUnits) * 100 + "%",
                          background: "#2f5e9e"
                        }}
                      />
                    </span>
                    <span className="type-count num">{m.units}</span>
                    <span className="type-vol num">{fmtMoney(m.volume)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <DonutChart
                items={monthlyBreakdown.map((m, i) => ({
                  label: m.label,
                  value: m.units,
                  color: MONTH_COLORS[i % MONTH_COLORS.length]
                }))}
              />
            )}
          </div>
        )}

        {/* funnel */}
        <div className="dashboard-funnel">
          <div className="funnel-head">
            <h3>Pipeline Funnel</h3>
            <div className="funnel-toggle">
              <button
                className={"button" + (chart === "bar" ? " active" : "")}
                onClick={() => setChart("bar")}
              >
                Bar
              </button>
              <button
                className={"button" + (chart === "pie" ? " active" : "")}
                onClick={() => setChart("pie")}
              >
                Pie
              </button>
            </div>
          </div>
          {chart === "bar" ? (
            <div className="funnel-bars">
              {FUNNEL_STAGES.map(s => (
                <div key={s} className="funnel-row">
                  <span className="funnel-name">{s}</span>
                  <div className="funnel-track">
                    <div
                      className="funnel-fill"
                      style={{
                        width: (cur.stageCounts[s] / maxStage) * 100 + "%",
                        background: STAGE_COLORS[s]
                      }}
                    />
                  </div>
                  <span className="funnel-count">{cur.stageCounts[s]}</span>
                </div>
              ))}
            </div>
          ) : (
            <Pie stageCounts={cur.stageCounts} total={funnelTotal} />
          )}
        </div>

        {/* Your own live, openable loan files (retail LO or wholesale AE) */}
        {isOwnBook && myDocs && myDocs.length > 0 && (
          <div className="dashboard-loans live-files-block">
            <h4 className="live-files-title">
              Your live loan files — click to open
            </h4>
            <table className="people-table">
              <thead>
                <tr>
                  <th>Loan File</th>
                  <th className="r">Purpose</th>
                  <th className="r">Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {myDocs.map(doc => (
                  <tr
                    key={doc.id}
                    className="drillable"
                    onClick={() =>
                      // Open the loan file in a NEW tab so the dashboard stays put
                      // (managers/AEs keep their dashboard view while reviewing).
                      window.open(
                        `/loan-tool/${doc.id}/applicant-info`,
                        "_blank",
                        "noopener"
                      )
                    }
                  >
                    <td>{doc.name} ›</td>
                    <td className="r">{DOCUMENT_TYPES[doc.type] || ""}</td>
                    <td className="r">
                      {(doc.pipelineData && doc.pipelineData.status) || "—"}
                    </td>
                    <td className="num">Open</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {lostAgg.count > 0 && (
          <div className="dashboard-lost" style={{ marginTop: "1.5rem" }}>
            <div className="people-head">
              <h3>Lost-Loan Intelligence</h3>
            </div>
            <p className="loans-note" style={{ marginTop: 0 }}>
              Who we lost to, at what rate — internal, from the Summary Sheet ·{" "}
              {nodeName(currentId)} · {period.toUpperCase()}
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))",
                gap: "12px", margin: "12px 0 18px"
              }}
            >
              {[
                ["Volume lost", fmtMoney(lostAgg.vol), lostAgg.count + " deals"],
                ["Lost to pricing", lostAgg.pricingPct + "%", lostAgg.pricing + " of " + lostAgg.count],
                ["Avg rate gap", lostAgg.avgGap == null ? "—" : (lostAgg.avgGap >= 0 ? "+" : "") + lostAgg.avgGap.toFixed(2) + "%", "we were higher"],
                ["Top competitor", lostAgg.topLender, fmtMoney(lostAgg.topLenderVol) + " won"]
              ].map((k, i) => (
                <div key={i} style={{ background: "#f6f7f9", borderRadius: "10px", padding: "12px 14px" }}>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>{k[0]}</div>
                  <div style={{ fontSize: "22px", fontWeight: 500, color: i === 0 || i === 2 ? "#b23b3b" : "#111" }}>{k[1]}</div>
                  <div style={{ fontSize: "12px", color: "#9aa0a6" }}>{k[2]}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>Who&#39;s beating us — by lender</div>
                {lostAgg.lenderOrder.slice(0, 6).map((n, i) => {
                  const max = lostAgg.byLender[lostAgg.lenderOrder[0]] || 1;
                  const w = Math.round((lostAgg.byLender[n] / max) * 100);
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px" }}>
                      <div style={{ width: "84px", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n}</div>
                      <div style={{ flex: 1, background: "#eef0f2", borderRadius: "6px", height: "16px" }}>
                        <div style={{ width: w + "%", background: "#b23b3b", height: "16px", borderRadius: "6px" }} />
                      </div>
                      <div style={{ width: "72px", textAlign: "right", fontSize: "12px", fontWeight: 500 }}>{fmtMoney(lostAgg.byLender[n])}</div>
                    </div>
                  );
                })}
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "8px" }}>Lost volume — by loan type</div>
                {[["Purchase", lostAgg.pV, lostAgg.pN, TXN_COLORS.Purchase], ["Refinance", lostAgg.rV, lostAgg.rN, TXN_COLORS.Refinance]].map((t, i) => {
                  const max = Math.max(lostAgg.pV, lostAgg.rV, 1);
                  const w = Math.round((t[1] / max) * 100);
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px" }}>
                      <div style={{ width: "74px", fontSize: "12px" }}>{t[0]}</div>
                      <div style={{ flex: 1, background: "#eef0f2", borderRadius: "6px", height: "16px" }}>
                        <div style={{ width: w + "%", background: t[3], height: "16px", borderRadius: "6px" }} />
                      </div>
                      <div style={{ width: "96px", textAlign: "right", fontSize: "12px", fontWeight: 500 }}>{fmtMoney(t[1])} ({t[2]})</div>
                    </div>
                  );
                })}
              </div>
            </div>
            <table className="people-table" style={{ marginTop: "16px" }}>
              <thead>
                <tr>
                  <th>Borrower</th>
                  <th className="num">Our rate</th>
                  <th className="r">Lost to</th>
                  <th className="num">Rate gap</th>
                  <th className="r">Reason</th>
                </tr>
              </thead>
              <tbody>
                {lostAgg.rows.slice(0, 12).map((l, i) => {
                  const g = l.ourRate != null && l.theirRate != null
                    ? (l.ourRate - l.theirRate >= 0 ? "+" : "") + (l.ourRate - l.theirRate).toFixed(3).replace(/0$/, "") + "%"
                    : "—";
                  return (
                    <tr key={i}>
                      <td>{l.borrower || "—"}<div style={{ fontSize: "11px", color: "#9aa0a6" }}>{l.loanType} · {fmtMoney(l.amount || 0)}</div></td>
                      <td className="num">{l.ourRate != null ? l.ourRate.toFixed(3) + "%" : "—"}</td>
                      <td className="r">{l.competitor || "—"}<div style={{ fontSize: "11px", color: "#9aa0a6" }}>{l.theirRate != null ? l.theirRate.toFixed(3) + "%" : "—"}{l.theirPoints ? " @ " + l.theirPoints + " pts" : ""}</div></td>
                      <td className="num" style={{ color: "#b23b3b" }}>{g}</td>
                      <td className="r">{l.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* drill: reports leaderboard OR loan list */}
        {drillItems ? (
          <div className="dashboard-people">
            <div className="people-head">
              <h3>
                {nodeType === "person" && mode === RETAIL && "Team"}
                {nodeType === "person" && mode === WHOLESALE &&
                  (drillItems[0] && drillItems[0].role === "Broker Company"
                    ? "Broker Companies"
                    : "Account Executives")}
                {nodeType === "brokerCompany" && "Broker Loan Officers"}
              </h3>
              <input
                className="people-search"
                placeholder="Search name / region / role…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <table className="people-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Region</th>
                  <th className="num">Submit→Close</th>
                  <th className="num">Funded</th>
                  <th className="num">Units</th>
                  <th className="num">Open</th>
                </tr>
              </thead>
              <tbody>
                {shownReports.map(it => (
                  <tr
                    key={it.key}
                    className="drillable"
                    onClick={it.drillTo}
                  >
                    <td>{it.name} ›</td>
                    <td>{it.role}</td>
                    <td>{it.region}</td>
                    <td className="num">{fmtPct(it.r.s2c)}</td>
                    <td className="num">{fmtMoney(it.r.fundedVol)}</td>
                    <td className="num">{it.r.closed}</td>
                    <td className="num">{it.r.openUnits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dashboard-loans">
            <div className="people-head">
              <h3>
                {nodeName(currentId)} — Loans (
                {mode === RETAIL ? "Retail" : "Wholesale"})
              </h3>
              {isOwnBook && (
                <button className="button" onClick={() => navigate("/")}>
                  Open Pipeline
                </button>
              )}
            </div>

            <h4 className="seeded-loans-title">
              Reported pipeline (analytics)
            </h4>
            <table className="people-table">
              <thead>
                <tr>
                  {mode === WHOLESALE && <th>Broker Company</th>}
                  {mode === WHOLESALE && <th>Broker LO</th>}
                  <th className="r">Purpose</th>
                  <th className="r">Loan Type</th>
                  <th className="r">Stage</th>
                  <th className="num">Amount</th>
                  <th className="num">Credit</th>
                  <th className="num">In Process</th>
                  <th className="num">Days Since Submit to U/W</th>
                  <th className="num">Closed</th>
                </tr>
              </thead>
              <tbody>
                {typedLoans
                  .slice()
                  .sort((a, b) => (b.submittedDate || "").localeCompare(a.submittedDate || ""))
                  .map((l, i) => {
                    const co = model.brokerCompanyById[l.brokerCompanyId];
                    const blo = model.brokerLoById[l.brokerLoId];
                    return (
                    <tr key={i}>
                      {mode === WHOLESALE && (
                        <td>{co ? co.companyName : "—"}</td>
                      )}
                      {mode === WHOLESALE && (
                        <td>{blo ? blo.name : "—"}</td>
                      )}
                      <td className="r">{l.loanType}</td>
                      <td className="r">{l.mortgageType}</td>
                      <td className="r">
                        <span
                          className="stage-dot"
                          style={{ background: STAGE_COLORS[l.stage] || "#888" }}
                        />
                        {l.stage}
                      </td>
                      <td className="num">{fmtMoney(l.amount)}</td>
                      <td className="num">{l.credit || "—"}</td>
                      <td className="num">
                        {isPreSubmission(l) && daysSince(l.startedDate, asOf) != null
                          ? daysSince(l.startedDate, asOf) + "d"
                          : "—"}
                      </td>
                      <td className="num">
                        {!isPreSubmission(l) && daysSince(l.submittedDate, asOf) != null
                          ? daysSince(l.submittedDate, asOf) + "d"
                          : "—"}
                      </td>
                      <td className="num">{fmtDate(l.closedDate)}</td>
                    </tr>
                    );
                  })}
              </tbody>
            </table>
            <p className="loans-note">
              Rows above are reported pipeline analytics. Editable loan files
              open from “Your live loan files” or the Pipeline.
            </p>
          </div>
        )}
      </div>
    </StandardTemplate>
  );
}

// Generic SVG donut for {label, value, color, onClick?, selected?} items.
function DonutChart({ items }) {
  const segs = (items || []).filter(it => it.value > 0);
  const total = segs.reduce((a, b) => a + b.value, 0) || 1;
  const radius = 80;
  const cx = 100;
  const cy = 100;
  let acc = 0;
  function point(frac) {
    const angle = 2 * Math.PI * frac - Math.PI / 2;
    return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
  }
  const arcs = segs.map(it => {
    const start = acc;
    acc += it.value / total;
    return { it, start, end: acc };
  });
  return (
    <div className="funnel-pie">
      <svg viewBox="0 0 200 200" width="200" height="200">
        {arcs.map((a, i) => {
          const [x1, y1] = point(a.start);
          const [x2, y2] = point(a.end);
          const large = a.end - a.start > 0.5 ? 1 : 0;
          const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`;
          return (
            <path
              key={i}
              d={d}
              fill={a.it.color}
              stroke="#fff"
              strokeWidth="1"
              style={{ cursor: a.it.onClick ? "pointer" : "default" }}
              onClick={a.it.onClick}
            />
          );
        })}
        <circle cx={cx} cy={cy} r="40" fill="var(--card-bg, #fff)" />
      </svg>
      <div className="pie-legend">
        {segs.map((it, i) => (
          <div
            key={i}
            className={"pie-legend-row" + (it.selected ? " selected" : "")}
            style={{ cursor: it.onClick ? "pointer" : "default" }}
            onClick={it.onClick}
          >
            <span className="pie-swatch" style={{ background: it.color }} />
            {it.label} <span className="num">{it.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Simple SVG donut for the funnel pie view.
function Pie({ stageCounts, total }) {
  let acc = 0;
  const radius = 80;
  const cx = 100;
  const cy = 100;
  const segments = FUNNEL_STAGES.map(s => {
    const value = stageCounts[s];
    const frac = value / total;
    const start = acc;
    acc += frac;
    return { s, value, start, end: acc };
  }).filter(seg => seg.value > 0);

  function point(frac) {
    const angle = 2 * Math.PI * frac - Math.PI / 2;
    return [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
  }

  return (
    <div className="funnel-pie">
      <svg viewBox="0 0 200 200" width="200" height="200">
        {segments.map(seg => {
          const [x1, y1] = point(seg.start);
          const [x2, y2] = point(seg.end);
          const large = seg.end - seg.start > 0.5 ? 1 : 0;
          const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`;
          return <path key={seg.s} d={d} fill={STAGE_COLORS[seg.s]} stroke="#fff" strokeWidth="1" />;
        })}
        <circle cx={cx} cy={cy} r="40" fill="var(--card-bg, #fff)" />
      </svg>
      <div className="pie-legend">
        {segments.map(seg => (
          <div key={seg.s} className="pie-legend-row">
            <span className="pie-swatch" style={{ background: STAGE_COLORS[seg.s] }} />
            {seg.s} <span className="num">{seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
